/**
 * Local (device) drafts for the signed-in writing player.
 * Key = signed-in user id + student profile id + quiz id, so siblings on one
 * parent account never share a draft.
 */
const PREFIX = "lurneax:writing-draft:";

export function writingDraftKey(userId: string, studentId: string, quizId: string) {
  return `${PREFIX}${userId}:${studentId}:${quizId}`;
}

export function readWritingDraft(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeWritingDraft(key: string, text: string) {
  try {
    if (text) window.localStorage.setItem(key, text);
    else window.localStorage.removeItem(key);
  } catch {
    /* storage full or blocked: ignore */
  }
}

export function clearWritingDraft(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Removes ONLY the signing-out user's draft keys from this device.
 * Never touches drafts belonging to other accounts on the same device
 * (shared family computer). If the signed-in user id can't be determined,
 * nothing is removed. All storage access is wrapped so a storage error
 * never breaks sign-out.
 */
export async function clearAllWritingDrafts(userId?: string) {
  try {
    let uid = userId;
    if (!uid) {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      uid = data.session?.user?.id;
    }
    if (!uid) return; // can't attribute keys — leave every user's drafts alone
    const userPrefixes = [`${PREFIX}${uid}:`, `lurneax:attempt-draft:${uid}:`];
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && userPrefixes.some(prefix => k.startsWith(prefix))) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore — never break sign-out on a storage error */
  }
}
